import React, { FC, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'

import { useUpload } from 'core/upload/api'
import { useShowMessage, useApiError } from 'shared/hooks'

import { useAuth } from 'core/auth'
import { Spin, Upload, Input, Modal } from 'antd'
import { BackIcon, PageContainer } from 'components'

import add from './assets/add.png'

import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface'

export const SingleNft: FC = () => {
    const navigate = useNavigate()
    const {
        methods: { error, success }
    } = useShowMessage()
    const {
        methods: { handleError }
    } = useApiError('SingleNft', error as any)
    const {
        loading,
        methods: { upload }
    } = useUpload()
    const { account } = useAuth()

    const [previewVisible, setPreviewVisible] = useState(false)
    const [previewImage, setPreviewImage] = useState('')
    const [previewTitle, setPreviewTitle] = useState('')
    const [fileList, setFileList] = useState<UploadFile[]>([])
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [tags, setTags] = useState('')
    const [url, setUrl] = useState('')

    const handleCancel = () => setPreviewVisible(false)

    const handlePreview = useCallback(
        async (file: UploadFile) => {
            setPreviewImage(file.thumbUrl as string)
            setPreviewVisible(true)
            setPreviewTitle(
                name || file.name || file.thumbUrl!.substring(file.thumbUrl!.lastIndexOf('/') + 1)
            )
        },
        [name]
    )

    const props: UploadProps = {
        listType: 'picture-card',
        className: 'upload-style',
        accept: '.bmp, .jpg, .jpeg, .png, .gif, .mp4, .mov',
        onRemove: file => {
            const index = fileList.indexOf(file)
            const newFileList = fileList.slice()
            newFileList.splice(index, 1)
            setFileList(newFileList)
        },
        onPreview: handlePreview,
        beforeUpload: file => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = e => {
                ;(file as any).thumbUrl = e.target?.result
                setFileList([...fileList, file])
            }

            return false
        },
        fileList
    }

    const handleSubmit = useCallback(() => {
        if (!account) return
        if (fileList.length <= 0 || !tags || !name || !description) {
            error('Please improve the information and submit')
            return
        }
        if (fileList[0] && (fileList[0].size as number) / 1024 / 1024 > 200) {
            error('Image size exceeding the limit')
            return
        }
        const formData = new FormData()
        formData.append('media_file', fileList[0] as RcFile)
        formData.append('name', name)
        formData.append('owner', account)
        formData.append('description', description)
        formData.append('tags', tags)
        formData.append('external_url', url)
        upload(formData as any)
            .then(() => {
                setFileList([])
                setName('')
                setDescription('')
                setTags('')
                success('Upload successfully')
            })
            .catch(err => {
                handleError('Upload failure')
            })
    }, [account, description, error, fileList, handleError, name, success, tags, upload, url])

    return (
        <Spin spinning={loading}>
            <Container>
                <div className="flex items-center">
                    <BackIcon onClick={() => navigate(-1)} />
                    <div className="title">Media File</div>
                </div>
                <div className="item">
                    <div className="label">
                        <div>Upload Image or video</div>
                        <div className="tip">
                            File types supported: JPG, PNG, GIF, SVG, MP4, MOV, Max size: 200MB
                        </div>
                    </div>
                    <Upload {...props}>
                        {fileList.length < 1 && (
                            <div className="uploadBtn">
                                <img src={add} alt="" />
                            </div>
                        )}
                    </Upload>
                    <Modal
                        visible={previewVisible}
                        title={previewTitle}
                        footer={null}
                        onCancel={handleCancel}
                    >
                        <img alt="example" style={{ width: '100%' }} src={previewImage} />
                    </Modal>
                </div>
                <div className="item">
                    <div className="label">
                        <div>NFT Name</div>
                    </div>
                    <Input
                        style={{ width: '100%', height: '40px' }}
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                </div>
                <div className="item">
                    <div className="label">
                        <div>Description</div>
                    </div>
                    <Input.TextArea
                        style={{ width: '100%', height: '80px' }}
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                    />
                </div>
                <div className="item">
                    <div className="label">
                        <div>Owner Address</div>
                    </div>
                    <Input style={{ width: '100%', height: '40px' }} disabled value={account || ''} />
                </div>
                <div className="item">
                    <div className="label">
                        <div>External Url</div>
                    </div>
                    <Input
                        style={{ width: '100%', height: '40px' }}
                        value={url}
                        onChange={e => setUrl(e.target.value)}
                    />
                </div>
                <div className="item">
                    <div className="label">
                        <div>Tag</div>
                    </div>
                    <Input
                        style={{ width: '100%', height: '40px' }}
                        value={tags}
                        onChange={e => setTags(e.target.value)}
                        placeholder={'separate tag with comma'}
                    />
                </div>
                <div className="btn" onClick={handleSubmit}>
                    Submit
                </div>
            </Container>
        </Spin>
    )
}

const Container = styled(PageContainer)`
    width: 580px;
    padding: 0;
    padding-bottom: 80px;
    .title {
        margin-left: 14px;
        width: 100%;
        height: 128px;
        line-height: 128px;
        font-family: Roboto-Medium;
        font-size: 36px;
        color: #000000;
        letter-spacing: 0;
    }
    .item {
        margin-bottom: 30px;
        .label {
            margin-bottom: 10px;
            font-family: HelveticaNeue-Medium;
            font-size: 18px;
            color: #000000;
            line-height: 26px;
        }
        .tip {
            font-family: Roboto-Regular;
            font-size: 12px;
            color: #000000;
        }
    }
    .uploadBtn {
        img {
            width: 50px;
            height: 50px;
        }
    }
    .ant-input {
        font-family: Roboto-Regular;
        font-size: 18px;
        &::placeholder {
            font-family: Roboto-Regular;
            font-size: 16px;
            color: #c2c2c2;
        }
    }
    .upload-style {
        .ant-upload-list-picture-card-container {
            width: 320px;
            height: 180px;
        }
        .ant-upload.ant-upload-select-picture-card {
            background-color: #ffffff;
        }
        .ant-upload {
            width: 320px;
            height: 180px;
        }
    }
    .btn {
        width: 100%;
        height: 50px;
        font-family: Roboto-Medium;
        font-size: 25.33px;
        color: #ffffff;
        letter-spacing: 0;
        text-align: center;
        line-height: 50px;
        background: #000000;
        border-radius: 35.18px;
        cursor: pointer;
    }
    @media (max-width: 768px) {
        padding: 0 20px 40px 20px;
        width: 100%;
    }
`
